import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Joi from 'joi';

import {
    useCreateInvestmentFundMutation,
    useGetInvestmentFundQuery,
    useGetPromotersQuery,
    useGetRolesQuery,
    useGetSGPsQuery,
    useGetSubscriptionsQuery,
    useUpdateInvestmentFundMutation,
} from 'api/main';
import {
    bicValidationSchema,
    checkImageFile,
    formatIBANOnType,
    getFileExtension,
    validateFileSize,
    validateSchema,
} from 'utils/helpers';
import { bicValidationMessages, fundTypes, roleCategories } from 'utils/constants';
import { fundSchemas } from 'schemas';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CheckboxLabel,
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    InfoButton,
    Loader,
    SuccessMessage,
} from 'components';
import { Box, InputAdornment, MenuItem, Typography } from '@mui/material';
import FundFile from './FundFile';
import { useNavigate } from 'react-router-dom';

const containerWidth = { lg: '50%', md: '100%', xl: '35%' };

const initFundForm = () => ({
    id: '',
    name: '',
    type: '',
    sgp: '',
    promoter: '',
    evergreen: false,
    allowedSubscriptionCreation: true,
    iban: '',
    swift: '',
    bankName: '',
    maximumAdmissionFee: '',
    firstInstalmentPercentage: '',
    initialDurationCommercialization: '',
    firstInstalmentDelay: '',
    taxBase: '',
    numberOfDecimals: '',
    fiscal: false,
    professional: false,
    externallyManaged: false,
    externallyManagedBy: '',
    collectionEndDate: moment(),
    eligibleForConditionalSum: false,
    fundRegulation: '',
    image: '',
    mergedDici: '',
    eligibleCustomers: '',
    taxBaseText: '',
    eligibleCountriesWithSubdistributions: '',
    eligibleCountriesWithoutSubdistributions: '',
    webinarRegistrationUrl: '',
    replayWebinarUrl: '',
});

const renderSGP = sgp => (
    <MenuItem key={sgp.id} value={sgp.id}>
        {sgp.name}
    </MenuItem>
);

const FundInfo = ({ fundId }) => {
    const navigate = useNavigate();
    const { data: roles = [] } = useGetRolesQuery();
    const {
        data: fund,
        isLoading: fundLoading,
        refetch,
    } = useGetInvestmentFundQuery({ id: fundId, checkSubscriptions: true }, { skip: !fundId });
    const { data: { sgps = [] } = { sgps: [], lastPage: 1 }, isLoading: sgpsLoading } =
        useGetSGPsQuery({});
    const { data: { promoters = [] } = { promoters: [] }, isLoading: promotersLoading } =
        useGetPromotersQuery();
    const [
        updateInvestmentFund,
        {
            isLoading: updateLoading,
            isSuccess: successfullyUpdated,
            reset: resetUpdate,
            error: updateError,
        },
    ] = useUpdateInvestmentFundMutation();
    const [
        createInvestmentFund,
        { isLoading: createLoading, isSuccess: successfullyCreated, error: createError },
    ] = useCreateInvestmentFundMutation();

    const investorRoles = useMemo(
        () => roles.filter(r => r.category?.name === roleCategories.INVESTORS),
        [roles]
    );
    const {
        data: { subscriptions: subscriptionList = [] } = { subscriptions: [] },
        isFetching: subscriptionsFetching,
    } = useGetSubscriptionsQuery(
        {
            role: investorRoles.map(role => role.id),
            itemsPerPage: 5,
            product: [fund?.id],
        },
        { skip: !roles.length || !fund?.id }
    );

    const [form, setForm] = useState(initFundForm());
    const [fileSizeErrors, setFileSizeErrors] = useState(initFundForm());
    const [fileTypeErrors, setFileTypeErrors] = useState(initFundForm());
    const [errors, setErrors] = useState({
        name: '',
        type: '',
        sgp: '',
        promoter: '',
        webinarRegistrationUrl: '',
        replayWebinarUrl: '',
    });

    const schema = useMemo(
        () => ({
            name: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            type: Joi.any().invalid('').messages({ 'any.invalid': texts.CHOISIR_OPTION }),
            evergreen: Joi.bool(),
            allowedSubscriptionCreation: Joi.bool(),
            sgp: Joi.any().invalid('').messages({ 'any.invalid': texts.CHOISIR_OPTION }),
            promoter: Joi.any().invalid('').messages({ 'any.invalid': texts.CHOISIR_OPTION }),
            iban: Joi.string()
                .required()
                .replace(/\s/g, '')
                .invalid(fund?.bankAccountNumber || '')
                .messages({
                    'string.empty': texts.SAISIR_IBAN,
                    'any.invalid': texts.IBAN_IS_THE_SAME,
                }),
            swift: Joi.string().custom(bicValidationSchema).messages(bicValidationMessages),
            bankName: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            maximumAdmissionFee: Joi.number().min(0).max(100).required().messages({
                'number.base': texts.SAISIR_LE_CHAMP,
                'number.max': texts.INCORRECT_INPUT,
            }),
            firstInstalmentPercentage: Joi.number().min(0).max(100).required().messages({
                'number.base': texts.SAISIR_LE_CHAMP,
                'number.min': texts.INCORRECT_INPUT,
                'number.max': texts.INCORRECT_INPUT,
            }),
            initialDurationCommercialization: Joi.number().min(1).required().messages({
                'number.base': texts.SAISIR_LE_CHAMP,
                'number.min': texts.INCORRECT_INPUT,
                'number.max': texts.INCORRECT_INPUT,
            }),
            firstInstalmentDelay: Joi.number().min(0).required().messages({
                'number.base': texts.SAISIR_LE_CHAMP,
                'number.min': texts.INCORRECT_INPUT,
                'number.max': texts.INCORRECT_INPUT,
            }),
            taxBase: Joi.number().min(0).max(100).required().messages({
                'number.base': texts.SAISIR_LE_CHAMP,
                'number.min': texts.INCORRECT_INPUT,
                'number.max': texts.INCORRECT_INPUT,
            }),
            numberOfDecimals: Joi.number().min(0).max(9).integer().required().messages({
                'any.required': texts.SAISIR_LE_CHAMP,
                'number.integer': texts.INTEGER_NUMBER_ERROR,
                'number.min': texts.DIGIT_ERROR_MESSAGE,
                'number.max': texts.DIGIT_ERROR_MESSAGE,
            }),
            externallyManaged: Joi.any(),
            externallyManagedBy: Joi.when('externallyManaged', {
                is: true,
                then: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
                otherwise: Joi.any(),
            }),
            eligibleCustomers: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            taxBaseText: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            eligibleCountriesWithSubdistributions: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            eligibleCountriesWithoutSubdistributions: Joi.string()
                .required()
                .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            webinarRegistrationUrl: Joi.string()
                .uri()
                .allow('')
                .messages({ 'string.uri': texts.URL_ERROR }),
            replayWebinarUrl: Joi.string()
                .uri()
                .allow('')
                .messages({ 'string.uri': texts.URL_ERROR }),
        }),
        [fund]
    );

    const handleChange = useCallback(
        key => e => {
            resetUpdate();
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        [resetUpdate]
    );
    const handleCheckboxChange = useCallback(
        key => e => {
            resetUpdate();
            setForm(prevState => ({
                ...prevState,
                [key]: e.target.checked,
                externallyManagedBy:
                    key === 'externallyManaged' ? '' : prevState.externallyManagedBy,
            }));
        },
        [resetUpdate]
    );

    const handleIBANChange = useCallback(
        e => {
            resetUpdate();
            setForm(prevState => ({ ...prevState, iban: formatIBANOnType(e.target) }));
            setErrors({ iban: '', rib: '', swift: '' });
        },
        [resetUpdate]
    );
    const handleBICChange = useCallback(
        ({ target }) => {
            resetUpdate();
            setForm(prevState => ({
                ...prevState,
                swift: target.value ? target.value.toUpperCase().replace(/\s/g, '') : '',
            }));
        },
        [resetUpdate]
    );
    const handleDateChange = useCallback(
        key => newDate => {
            setForm(prevState => ({
                ...prevState,
                [key]: newDate.startOf('day').add(newDate.utcOffset(), 'm'),
            }));
        },
        []
    );
    const handleFileChange = useCallback(
        key => e => {
            const file = e.target.files[0];
            const fileExtension = getFileExtension(file);

            if (!validateFileSize(file)) {
                // set file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: texts.FILE_TOO_LARGE }));
                return;
            } else {
                // clear file size error
                setFileSizeErrors(prevState => ({ ...prevState, [key]: '' }));
            }
            let hasFileTypeError = true;
            if (key === 'image') {
                checkImageFile(file, () => {
                    hasFileTypeError = false;
                });
            } else if (fileExtension === 'pdf') {
                hasFileTypeError = false;
            }

            if (hasFileTypeError) {
                setFileTypeErrors(prevState => ({
                    ...prevState,
                    [key]: texts.FILE_TYPE_INCORRECT,
                }));
            } else {
                setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
                setForm(prevState => ({ ...prevState, [key]: file }));
            }
        },
        []
    );

    const handleFileDelete = useCallback(
        key => () => {
            setFileTypeErrors(prevState => ({ ...prevState, [key]: '' }));
            setForm(prevState => ({ ...prevState, [key]: null }));
        },
        []
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        if (valid) {
            setErrors({ name: '', webinarRegistrationUrl: '', replayWebinarUrl: '' });
            const payload = fundSchemas.createInvestmentFund(form);
            if (fundId) {
                updateInvestmentFund({
                    ...payload,
                    id: form.id,
                    numberOfDecimals: +form.numberOfDecimals,
                    fundRegulation:
                        form.fundRegulation instanceof File ? form.fundRegulation : undefined,
                    image: form.image instanceof File ? form.image : undefined,
                    mergedDici: form.mergedDici instanceof File ? form.mergedDici : undefined,
                    fundRegulationId:
                        form.fundRegulation instanceof File ? fund?.fundRegulation?.id : undefined,
                    imageId: form.image instanceof File ? fund?.image.id : undefined,
                    mergedDiciId:
                        form.mergedDici instanceof File ? fund?.mergedDici?.id : undefined,
                });
            } else {
                createInvestmentFund({ ...payload, id: undefined });
            }
        }
    }, [form, schema, updateInvestmentFund, fundId, createInvestmentFund, fund]);

    useEffect(() => {
        if (successfullyUpdated) {
            refetch();
        }
    }, [successfullyUpdated, refetch]);

    useEffect(() => {
        if (successfullyCreated) {
            // implement
            navigate('/fund-management');
        }
    }, [successfullyCreated, navigate]);

    useEffect(() => {
        if (createError) {
            let errorType = createError?.data?.['hydra:description'];
            if (errorType === 'iban: invalid_type') {
                setErrors(prevState => ({ ...prevState, iban: texts.IBAN_PAS_VALIDE }));
            }
        }
    }, [createError]);

    useEffect(() => {
        if (updateError) {
            let errorType = updateError?.data?.['hydra:description'];
            if (errorType === 'iban: invalid_type') {
                setErrors(prevState => ({ ...prevState, iban: texts.IBAN_PAS_VALIDE }));
            }
        }
    }, [updateError]);

    useEffect(() => {
        if (fund?.id) {
            setForm({
                id: fund?.id,
                name: fund?.name,
                type: fund?.type,
                evergreen: fund?.evergreen,
                allowedSubscriptionCreation: fund?.allowedSubscriptionCreation,
                sgp: fund?.sgp?.id,
                promoter: fund?.promoter?.id,
                swift: fund?.swift,
                iban: fund?.iban,
                bankName: fund?.bankName,
                maximumAdmissionFee: fund?.maximumAdmissionFee,
                firstInstalmentPercentage: fund?.firstInstalmentPercentage,
                initialDurationCommercialization: fund?.initialDurationCommercialization,
                firstInstalmentDelay: fund?.firstInstalmentDelay,
                taxBase: fund?.taxBase,
                numberOfDecimals: fund?.numberOfDecimals || '',
                fiscal: fund?.fiscal,
                professional: fund?.professional,
                externallyManaged: fund?.externallyManaged,
                externallyManagedBy: fund?.externallyManagedBy || '',
                collectionEndDate: moment(fund?.collectionEndDate),
                eligibleForConditionalSum: fund?.eligibleForConditionalSum,
                fundRegulation: fund?.fundRegulation,
                image: fund?.image,
                mergedDici: fund?.mergedDici,
                eligibleCustomers: fund?.eligibleCustomers,
                taxBaseText: fund?.taxBaseText,
                eligibleCountriesWithSubdistributions: fund?.eligibleCountriesWithSubdistributions,
                eligibleCountriesWithoutSubdistributions:
                    fund?.eligibleCountriesWithoutSubdistributions,
                webinarRegistrationUrl: fund?.webinarRegistrationUrl || '',
                replayWebinarUrl: fund?.replayWebinarUrl || '',
            });
        }
    }, [fund]);

    return (
        <Loader loading={fundLoading}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
                gap={2}
                paddingY={3}
            >
                <Box sx={{ width: containerWidth, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="600" mb={2}>
                        {texts.GENERAL_INFO}
                    </Typography>

                    <CustomTextField
                        label={texts.FUND_NAME}
                        value={form.name}
                        sx={{ width: '100%' }}
                        onChange={handleChange('name')}
                        error={errors.name}
                    />
                    <CustomSelect
                        fullWidth
                        label={texts.FUND_TYPE}
                        value={form.type}
                        //disable if the fund has at least one subscription
                        disabled={subscriptionList?.length > 0 || subscriptionsFetching}
                        onChange={handleChange('type')}
                        error={errors.type}
                        helperText={errors.type}
                    >
                        {Object.values(fundTypes).map(t => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.EVERGREEN}
                        checked={form.evergreen}
                        disabled={fund?.hasSubscriptions}
                        onChange={handleCheckboxChange('evergreen')}
                    />
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.ENABLE_SUBSCRIPTIONS}
                        checked={form.allowedSubscriptionCreation}
                        disabled={fund?.hasSubscriptions}
                        onChange={handleCheckboxChange('allowedSubscriptionCreation')}
                    />
                    <Loader loading={sgpsLoading} marginTop={2}>
                        <CustomSelect
                            fullWidth
                            label={texts.SGP_PROVIDER}
                            value={form.sgp}
                            onChange={handleChange('sgp')}
                            error={errors.sgp}
                            helperText={errors.sgp}
                        >
                            {sgps.map(renderSGP)}
                        </CustomSelect>
                    </Loader>
                    <Loader loading={promotersLoading}>
                        <CustomSelect
                            fullWidth
                            label={texts.PROMOTER}
                            value={form.promoter}
                            onChange={handleChange('promoter')}
                            error={errors.promoter}
                            helperText={errors.promoter}
                        >
                            {promoters.map(promoter => (
                                <MenuItem key={promoter.id} value={promoter.id}>
                                    {promoter.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Loader>
                    <CustomTextField
                        label={texts.IBAN}
                        onChange={handleIBANChange}
                        value={form.iban}
                        error={errors.iban}
                        showError
                        inputProps={{ maxLength: 42 }}
                    />
                    <CustomTextField
                        fullWidth
                        label={texts.BIC}
                        onChange={handleBICChange}
                        value={form.swift}
                        error={errors.swift}
                        inputProps={{ maxLength: 11 }}
                        infoButton={
                            <InfoButton
                                disableMarginBottom
                                title={texts.BIC_FORMAT_TITLE}
                                content={texts.BIC_FORMAT_CONTENT}
                            />
                        }
                    />
                    <CustomTextField
                        label={texts.BANK_NAME}
                        onChange={handleChange('bankName')}
                        value={form.bankName}
                        error={errors.bankName}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.MAX_ADMISSION_FEE}
                        onChange={handleChange('maximumAdmissionFee')}
                        value={form.maximumAdmissionFee}
                        error={errors.maximumAdmissionFee}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.FIRST_INSTALLMENT_PERCENTAGE}
                        onChange={handleChange('firstInstalmentPercentage')}
                        value={form.firstInstalmentPercentage}
                        error={errors.firstInstalmentPercentage}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.INITIAL_COMMERCIALIZATION_DURATION}
                        onChange={handleChange('initialDurationCommercialization')}
                        value={form.initialDurationCommercialization}
                        error={errors.initialDurationCommercialization}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.FIRST_INSTALLMENT_DELAY}
                        onChange={handleChange('firstInstalmentDelay')}
                        value={form.firstInstalmentDelay}
                        error={errors.firstInstalmentDelay}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.TAX_BASE}
                        onChange={handleChange('taxBase')}
                        value={form.taxBase}
                        error={errors.taxBase}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    <CustomTextField
                        type="number"
                        label={texts.NUMBER_OF_DECIMALS}
                        onChange={handleChange('numberOfDecimals')}
                        value={form.numberOfDecimals}
                        error={errors.numberOfDecimals}
                    />
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.FISCAL}
                        checked={form.fiscal}
                        onChange={handleCheckboxChange('fiscal')}
                    />
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.PROFESSIONAL}
                        checked={form.professional}
                        onChange={handleCheckboxChange('professional')}
                    />
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.EXTERNALLY_MANAGED_LABEL}
                        checked={form.externallyManaged}
                        onChange={handleCheckboxChange('externallyManaged')}
                    />
                    {form.externallyManaged && (
                        <CustomTextField
                            label={texts.EXTERNALLY_MANAGED_BY}
                            value={form.externallyManagedBy}
                            onChange={handleChange('externallyManagedBy')}
                            error={errors.externallyManagedBy}
                        />
                    )}
                    <CustomDatePicker
                        fullWidth
                        label={texts.COLLECTION_DATE}
                        value={form.collectionEndDate}
                        onChange={handleDateChange('collectionEndDate')}
                        showError={true}
                    />
                    <CheckboxLabel
                        sx={{ display: 'flex', alignItems: 'center' }}
                        label={texts.ELIGIBLE_FOR_CONDITIONAL_SUM}
                        checked={form.eligibleForConditionalSum}
                        onChange={handleCheckboxChange('eligibleForConditionalSum')}
                    />

                    {/* //#endregion */}

                    {/* //#region Essential Documents */}
                    <Typography variant="subtitle1" fontWeight="600" mb={2}>
                        {texts.ESSENTIAL_FUND_DOCUMENTS}
                    </Typography>
                    <FundFile
                        id="fund-regulation"
                        onChange={handleFileChange('fundRegulation')}
                        name={form.fundRegulation?.name || texts.FUND_REGULATIONS}
                        label={texts.FUND_REGULATIONS}
                        typeError={!!fileTypeErrors.fundRegulation}
                        sizeError={!!fileSizeErrors.fundRegulation}
                        file={form.fundRegulation}
                    />
                    <FundFile
                        id="fund-logo"
                        onChange={handleFileChange('image')}
                        name={form.image?.name || texts.LOGO}
                        label={texts.LOGO}
                        typeError={!!fileTypeErrors.image}
                        sizeError={!!fileSizeErrors.image}
                        file={form.image}
                    />
                    <FundFile
                        id="merged-dici"
                        onChange={handleFileChange('mergedDici')}
                        onDelete={handleFileDelete('mergedDici')}
                        name={form.mergedDici?.name || texts.MERGED_DICI}
                        label={texts.MERGED_DICI}
                        typeError={!!fileTypeErrors.mergedDici}
                        sizeError={!!fileSizeErrors.mergedDici}
                        file={form.mergedDici}
                    />
                    {/* //#endregion */}

                    {/* //#region Convention info */}
                    <Typography variant="subtitle1" fontWeight="600" mb={2}>
                        {texts.CONVENTION_INFO}
                    </Typography>

                    <CustomTextField
                        multiline
                        rows={3}
                        label={texts.ELIGIBLE_CUSTOMERS}
                        value={form.eligibleCustomers}
                        sx={{ width: '100%' }}
                        onChange={handleChange('eligibleCustomers')}
                        error={errors.eligibleCustomers}
                    />
                    <CustomTextField
                        multiline
                        rows={3}
                        label={texts.TAX_BASE}
                        value={form.taxBaseText}
                        sx={{ width: '100%' }}
                        onChange={handleChange('taxBaseText')}
                        error={errors.taxBaseText}
                    />
                    <CustomTextField
                        multiline
                        rows={3}
                        label={texts.ELIGIBLE_COUNTRIES_WOS}
                        value={form.eligibleCountriesWithoutSubdistributions}
                        sx={{ width: '100%' }}
                        onChange={handleChange('eligibleCountriesWithoutSubdistributions')}
                        error={errors.eligibleCountriesWithoutSubdistributions}
                    />
                    <CustomTextField
                        multiline
                        rows={3}
                        label={texts.ELIGIBLE_COUNTRIES_WS}
                        value={form.eligibleCountriesWithSubdistributions}
                        sx={{ width: '100%' }}
                        onChange={handleChange('eligibleCountriesWithSubdistributions')}
                        error={errors.eligibleCountriesWithSubdistributions}
                    />
                </Box>
                {/* //#endregion */}

                {/* //#region webinar */}
                <Box sx={{ width: containerWidth, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="600" mb={2}>
                        {texts.WEBINAR}
                    </Typography>
                    <CustomTextField
                        label={texts.INSCRIPTION_WEBINAR}
                        value={form.webinarRegistrationUrl}
                        sx={{ width: '100%' }}
                        onChange={handleChange('webinarRegistrationUrl')}
                        error={errors.webinarRegistrationUrl}
                    />
                    <CustomTextField
                        label={texts.REPLAY_WEBINAR}
                        value={form.replayWebinarUrl}
                        sx={{ width: '100%' }}
                        onChange={handleChange('replayWebinarUrl')}
                        error={errors.replayWebinarUrl}
                    />
                </Box>
                {/* //#endregion */}
                <ActionButton
                    loading={updateLoading || createLoading}
                    onClick={handleSubmit}
                    variant="contained"
                >
                    {texts.SAVE}
                </ActionButton>
                {successfullyUpdated && <SuccessMessage message={texts.INFO_SAVED} />}
            </Box>
        </Loader>
    );
};

FundInfo.propTypes = {
    fundId: PropTypes.number.isRequired,
};

export default FundInfo;
